import { useState } from "react";
import { useSearchParams } from "react-router";
import {
  Activity,
  BookMarked,
  Bot,
  CircleDollarSign,
  Database,
  FileQuestion,
  Layers,
  LayoutGrid,
  Megaphone,
  PenLine,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import { useAsync } from "../lib/useAsync";
import { fetchStats, type SubjectSummary } from "../api/worker";
import type { Range } from "../api/insights";
import { useMe } from "../components/MeProvider";
import { Card, Stat } from "../components/ui";
import { InsightsSection, RANGE_KEY, type Section } from "./insights/InsightsScreen";

type Tab = "bank" | Section;

const TABS: { key: Tab; label: string; icon: LucideIcon; need: "content" | "insights" }[] = [
  { key: "bank", label: "Question bank", icon: Database, need: "content" },
  { key: "overview", label: "Overview", icon: LayoutGrid, need: "insights" },
  { key: "learning", label: "Learning", icon: BookMarked, need: "insights" },
  { key: "revenue", label: "Revenue", icon: CircleDollarSign, need: "insights" },
  { key: "acquisition", label: "Acquisition", icon: Megaphone, need: "insights" },
  { key: "tutor", label: "Tutor quality", icon: Bot, need: "insights" },
  { key: "operations", label: "Operations", icon: Activity, need: "insights" },
];

/**
 * Every number in one place.
 *
 * Content workers see the question bank. Leadership (super admin, product
 * manager) also see the PrepAI sections; a product manager, who has no
 * content access, sees only those. The backend enforces both — this only
 * decides which tabs to offer.
 */
export function DashboardScreen() {
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<Range>(() => {
    try {
      const saved = Number(localStorage.getItem(RANGE_KEY));
      return saved === 7 || saved === 90 ? saved : 30;
    } catch {
      return 30;
    }
  });

  const tabs = TABS.filter((t) => (me ? me.can[t.need] : t.need === "content"));
  const requested = params.get("tab") as Tab | null;
  const tab: Tab = tabs.find((t) => t.key === requested)?.key ?? tabs[0]?.key ?? "bank";
  const pickRange = (r: Range) => {
    setRange(r);
    try {
      localStorage.setItem(RANGE_KEY, String(r));
    } catch {
      /* not remembered */
    }
  };
  const showRange = tab !== "bank" && tab !== "operations";

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-[22px] font-semibold lg:text-[26px]">Dashboard</h1>
          <p className="mt-1 text-[13px] text-text-muted">
            {me?.can.insights
              ? "Question bank and business health · Nigeria · aggregates only"
              : "Question bank overview"}
          </p>
        </div>
        {showRange ? (
          <div
            role="group"
            aria-label="Period"
            className="flex gap-1 self-start rounded-xl border border-border bg-surface p-1"
          >
            {([7, 30, 90] as Range[]).map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={range === r}
                onClick={() => pickRange(r)}
                className={`rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  range === r ? "bg-surface-high text-text" : "text-text-muted hover:text-text"
                }`}
              >
                {r} days
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {tabs.length > 1 ? (
        <div
          role="tablist"
          aria-label="Dashboard sections"
          className="mt-6 flex gap-1 overflow-x-auto border-b border-border"
        >
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              role="tab"
              type="button"
              aria-selected={tab === key}
              onClick={() => setParams(key === tabs[0]?.key ? {} : { tab: key }, { replace: true })}
              className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors ${
                tab === key
                  ? "border-accent text-text"
                  : "border-transparent text-text-muted hover:text-text"
              }`}
            >
              <Icon size={15} strokeWidth={1.8} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-6">
        {tab === "bank" ? <QuestionBank /> : <InsightsSection section={tab} range={range} />}
      </div>
    </div>
  );
}

function SubjectRow({ subject, max }: { subject: SubjectSummary; max: number }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 py-3 first:pt-0 last:pb-0">
      <span className="min-w-0 truncate text-[13.5px] font-medium">
        {subject.name}
        <span className="ml-2 rounded-md bg-surface-high px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
          {subject.examType}
        </span>
      </span>
      <span className="text-right text-[13px] tabular-nums text-text-muted">
        {subject.questionCount.toLocaleString()}
      </span>
      <span className="col-span-2 h-1.5 overflow-hidden rounded-full bg-surface-high">
        <span
          className="block h-full rounded-full bg-accent"
          style={{ width: `${max ? (subject.questionCount / max) * 100 : 0}%` }}
        />
      </span>
    </li>
  );
}

function QuestionBank() {
  const { data, loading } = useAsync(fetchStats);

  if (loading || !data) {
    return <p className="text-text-muted">Loading the question bank…</p>;
  }

  const subjects = [...data.subjects].sort((a, b) => b.questionCount - a.questionCount);
  const max = subjects[0]?.questionCount ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label="Total questions"
          value={data.totalQuestions.toLocaleString()}
          icon={Layers}
          tone="accent"
        />
        <Stat
          label="MCQ"
          value={(data.byType["mcq"] ?? 0).toLocaleString()}
          icon={FileQuestion}
          tone="info"
        />
        <Stat
          label="Essay"
          value={(data.byType["essay"] ?? 0).toLocaleString()}
          icon={PenLine}
          tone="warning"
        />
        <Stat
          label="Active"
          value={(data.byStatus["active"] ?? 0).toLocaleString()}
          icon={Activity}
          tone="positive"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <Card title="Questions by subject" subtitle={`${subjects.length} subjects`} icon={Layers}>
          {subjects.length ? (
            <ul className="divide-y divide-border">
              {subjects.map((s) => (
                <SubjectRow key={s.id} subject={s} max={max} />
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-text-muted">No subjects yet.</p>
          )}
        </Card>

        {data.appInstalls ? (
          <Card
            title="App installs"
            subtitle="Unique students who installed the app"
            icon={Smartphone}
          >
            <p className="font-[family-name:var(--font-display)] text-[32px] font-semibold leading-none tabular-nums">
              {data.appInstalls.total.toLocaleString()}
            </p>
            <ul className="mt-4 flex flex-col gap-2 text-[13px]">
              {["android", "ios", "desktop"].map((platform) => (
                <li key={platform} className="flex items-center justify-between">
                  <span className="text-text-muted">
                    {platform === "ios" ? "iOS" : platform[0]!.toUpperCase() + platform.slice(1)}
                  </span>
                  <span className="tabular-nums">
                    {(data.appInstalls?.byPlatform[platform] ?? 0).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
